import { Dispatch } from 'redux'
import { getApiErrorMessage, getCSRFToken } from '../../lib/utils'
import { ItemScopeUpdate } from '../../types/PurchaseItem'
import {
	UPDATEITEMS_ERROR,
	UPDATEITEMS_SENDING,
	UPDATEITEMS_SENT,
} from '../slices/updateItemsSlice'
import { NeoniApi, TrackedMutationOptions } from '../../lib/api'


export const updateItemsScope =
	(api: NeoniApi, items: ItemScopeUpdate[], { deferSuccess = false, signal, onSubmitted }: TrackedMutationOptions & { deferSuccess?: boolean } = {}) => async (dispatch: Dispatch) => {
		dispatch({
			type: UPDATEITEMS_SENDING,
		})

		try {
			const csrf = await getCSRFToken(signal)
			if (signal?.aborted) return false
			const payload = { items, _csrf: csrf }
			onSubmitted?.()
			const body = signal
				? await api.post('items/bulkUpdateScopes', payload, { signal })
				: await api.post('items/bulkUpdateScopes', payload)
			if (signal?.aborted) return false

			if (body.success === true) {
				if (!deferSuccess) {
					dispatch({
						type: UPDATEITEMS_SENT,
						payload: body.message,
					})
				}
				return true
			} else {
				// Issue #884 follow-up: UPDATEITEMS_ERROR's payload is typed PayloadAction<string>
				// (updateItemsSlice.ts) and CompanyRefineElectricity.tsx/CompanyRefineEmissions.tsx
				// render it directly as a React child - the previous `{ error: body.message }` wrapper
				// dispatched an object here, which crashes on any unsuccessful (success: false) response.
				dispatch({
					type: UPDATEITEMS_ERROR,
					payload: getApiErrorMessage({ body }, "Failed to update these items' scope. Please try again."),
				})
				return false
			}
		} catch (error) {
			if (signal?.aborted) return false
			// Issue #884 follow-up (mirrors #857 item 6 / #882's addPurchasesUploadActions.ts fix):
			// READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE now reject (lib/api.ts) with a
			// SerializableApiError instead of hanging forever, so this catch now actually runs for
			// those two codes. The previous `{ error: error }` wrapper dispatched the raw caught
			// error/exception object, which crashes the same direct-render consumers above.
			// getApiErrorMessage normalises this to a plain string.
			dispatch({
				type: UPDATEITEMS_ERROR,
				payload: getApiErrorMessage(error, "Failed to update these items' scope. Please try again."),
			})
			return false
		}
	}

export const updateItemsEmissionsType =
	(api: NeoniApi, items: {id: string, emission_type?: string|null}[], options?: TrackedMutationOptions) => async (dispatch: Dispatch) => {
		dispatch({
			type: UPDATEITEMS_SENDING,
		})

		try {
			const csrf = await getCSRFToken(options?.signal)
			if (options?.signal?.aborted) return false
			const payload = { items, _csrf: csrf }
			const { onSubmitted, ...requestOptions } = options ?? {}
			onSubmitted?.()
			const body = options
				? await api.put('items/emission_types', payload, requestOptions)
				: await api.put('items/emission_types', payload)
			if (options?.signal?.aborted) return false

			if (body.success === true) {
				dispatch({
					type: UPDATEITEMS_SENT,
					payload: body.message,
				})
				return true
			} else {
				// Issue #884 follow-up: see updateItemsScope above - same object-wrapping bug.
				dispatch({
					type: UPDATEITEMS_ERROR,
					payload: getApiErrorMessage({ body }, "Failed to update these items' emission type. Please try again."),
				})
				return false
			}
		} catch (error) {
			if (options?.signal?.aborted) return false
			// Issue #884 follow-up: see updateItemsScope above - same object-wrapping bug, now
			// actually reachable for READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE since #857 item 6.
			dispatch({
				type: UPDATEITEMS_ERROR,
				payload: getApiErrorMessage(error, "Failed to update these items' emission type. Please try again."),
			})
			return false
		}
	}
